import { useMemo, useState } from "react";
import { Grid, GridItem, SimpleGrid, Box, HStack, VStack, Text, IconButton, Heading, Skeleton, Wrap, WrapItem, Button, Badge, useDisclosure, useToast } from "@chakra-ui/react";
import { PageHeader, SectionCard, CurrencyText, EmptyState } from "../../../shared/ui";
import { ChevronLeft, ChevronRight, Plus, Repeat, CalendarPlus, AlertTriangle } from "../../../shared/icons";
import { DIAS_SEMANA_ABREV, formatarMesAno } from "../../../shared/lib/date";
import { STATUS_META, STATUS_ORDEM } from "../../plantoes/utils/statusPlantao";
import { useAuth } from "../../auth/hooks/useAuth";
import { useCalendarView } from "../hooks/useCalendarView";
import { usePlantoesQuery } from "../../plantoes/hooks/usePlantoesQuery";
import { useVinculosQuery } from "../../vinculos/hooks/useVinculosQuery";
import { useUpdatePlantaoStatusMutation } from "../../plantoes/hooks/useUpdatePlantaoStatusMutation";
import EditShiftModal from "../../plantoes/components/EditShiftModal";
import CancelShiftDialog from "../../plantoes/components/CancelShiftDialog";
import RegistrarPlantaoModal from "../../plantoes/components/RegistrarPlantaoModal";
import GenerateScheduleModal from "../../shiftPatterns/components/GenerateScheduleModal";
import ImportFromGoogleButton from "../../googleImport/components/ImportFromGoogleButton";
import CalendarDayCell from "../components/CalendarDayCell";
import CalendarViewSwitcher from "../components/CalendarViewSwitcher";
import VinculoFilterMenu from "../components/VinculoFilterMenu";
import WeekView from "../components/WeekView";
import DayListView from "../components/DayListView";
import { groupShiftsByDay } from "../utils/groupShiftsByDay";
import { buildDayList, buildMonthCalendar, filterShifts, groupShiftsByDate, isoDate, weekDates } from "../utils/calendarViews";
import CalendarEventModal from "../../calendarEvents/components/CalendarEventModal";
import DeleteCalendarEventDialog from "../../calendarEvents/components/DeleteCalendarEventDialog";
import { useCalendarEventsQuery } from "../../calendarEvents/hooks/calendarEventQueries";
import { groupEventsByDay, monthRange } from "../../calendarEvents/utils/calendarEventKinds";

const shortDate = (iso) => iso.split("-").reverse().slice(0, 2).join("/");

const NAV_LABELS = {
  mes: ["Mês anterior", "Próximo mês"],
  lista: ["Mês anterior", "Próximo mês"],
  semana: ["Semana anterior", "Próxima semana"],
};

export default function CalendarioPage() {
  const toast = useToast();
  const { user } = useAuth();
  const { view, date, year: ano, month: mes, today, setView, previous, next, goToday } = useCalendarView(user?.id);
  const [statusFiltro, setStatusFiltro] = useState("");
  // Vínculos escolhidos no filtro; vazio = todos. Combina com o status (E).
  const [vinculoIds, setVinculoIds] = useState([]);
  const isFiltered = Boolean(statusFiltro) || vinculoIds.length > 0;
  const week = useMemo(() => weekDates(date), [date]);

  // Tudo sai da listagem de plantões, filtrada aqui por status e vínculo, para que células,
  // Semana, Lista e o resumo do mês reflitam os mesmos filtros: os trabalhados no mês
  // (valor do dia e faturamento) e os com pagamento previsto no mês (que podem ter sido
  // trabalhados no mês anterior). A Semana busca o próprio intervalo, que pode cruzar meses.
  const workedQuery = usePlantoesQuery({ ano, mes });
  const paymentQuery = usePlantoesQuery({ anoPagamento: ano, mesPagamento: mes });
  const weekQuery = usePlantoesQuery({ dataInicio: week[0], dataFim: week[6] }, { enabled: view === "semana" });
  const workedShifts = useMemo(
    () => filterShifts(workedQuery.data?.plantoes ?? [], { status: statusFiltro, vinculoIds }),
    [workedQuery.data, statusFiltro, vinculoIds]
  );
  const paymentShifts = useMemo(
    () => filterShifts(paymentQuery.data?.plantoes ?? [], { status: statusFiltro, vinculoIds }),
    [paymentQuery.data, statusFiltro, vinculoIds]
  );
  const weekShiftsByDate = useMemo(
    () => groupShiftsByDate(filterShifts(weekQuery.data?.plantoes ?? [], { status: statusFiltro, vinculoIds })),
    [weekQuery.data, statusFiltro, vinculoIds]
  );
  const calendar = useMemo(
    () => buildMonthCalendar({ year: ano, month: mes, workedShifts, paymentShifts, today }),
    [ano, mes, workedShifts, paymentShifts, today]
  );
  const plantoesPorDia = useMemo(() => groupShiftsByDay(workedShifts), [workedShifts]);
  // Cor e endereço de cada vínculo, para o marcador e o "Como chegar" dos dias.
  const { data: vinculos } = useVinculosQuery();
  const vinculosById = useMemo(() => Object.fromEntries((vinculos ?? []).map((v) => [v.id, v])), [vinculos]);

  // Compromissos (folga, férias...) só aparecem nos dias: nunca entram em total e não são
  // filtrados por status nem por vínculo.
  const eventsRange = view === "semana" ? { de: week[0], ate: week[6] } : monthRange(ano, mes);
  const { data: compromissos } = useCalendarEventsQuery(eventsRange);
  const compromissosPorDia = useMemo(() => groupEventsByDay(compromissos ?? [], ano, mes), [compromissos, ano, mes]);
  const dayList = useMemo(
    () => buildDayList({ shifts: workedShifts, events: compromissos ?? [], from: eventsRange.de, to: eventsRange.ate }),
    [workedShifts, compromissos, eventsRange.de, eventsRange.ate]
  );
  const compromissoDisclosure = useDisclosure();
  const excluirCompromissoDisclosure = useDisclosure();
  const [compromissoEditando, setCompromissoEditando] = useState(null);
  const [compromissoExcluindo, setCompromissoExcluindo] = useState(null);
  const [dataCompromisso, setDataCompromisso] = useState(null);
  // `data`: "yyyy-MM-dd" do dia escolhido, ou null pelo botão do cabeçalho.
  const abrirNovoCompromisso = (data) => {
    setCompromissoEditando(null);
    setDataCompromisso(data);
    compromissoDisclosure.onOpen();
  };
  const abrirEditarCompromisso = (compromisso) => {
    setCompromissoEditando(compromisso);
    compromissoDisclosure.onOpen();
  };
  const abrirExcluirCompromisso = (compromisso) => {
    setCompromissoExcluindo(compromisso);
    excluirCompromissoDisclosure.onOpen();
  };

  const statusMutation = useUpdatePlantaoStatusMutation();
  const [plantaoEditando, setPlantaoEditando] = useState(null);
  const [plantaoReagendando, setPlantaoReagendando] = useState(null);
  const [plantaoCancelando, setPlantaoCancelando] = useState(null);
  const editarDisclosure = useDisclosure();
  const reagendarDisclosure = useDisclosure();
  const cancelarDisclosure = useDisclosure();
  const registrarDisclosure = useDisclosure();
  const gerarEscalaDisclosure = useDisclosure();
  const [dataRegistro, setDataRegistro] = useState(null);
  // Plantão de origem da ação "Duplicar" (null = cadastro normal).
  const [plantaoDuplicando, setPlantaoDuplicando] = useState(null);

  // `data`: "yyyy-MM-dd" do dia escolhido, ou null pelo botão do cabeçalho.
  const abrirRegistrar = (data) => {
    setPlantaoDuplicando(null);
    setDataRegistro(data);
    registrarDisclosure.onOpen();
  };
  const abrirDuplicar = (plantao) => {
    setDataRegistro(null);
    setPlantaoDuplicando(plantao);
    registrarDisclosure.onOpen();
  };

  const abrirEditar = (plantao) => {
    setPlantaoEditando(plantao);
    editarDisclosure.onOpen();
  };
  const abrirReagendar = (plantao) => {
    setPlantaoReagendando(plantao);
    reagendarDisclosure.onOpen();
  };
  const abrirCancelar = (plantao) => {
    setPlantaoCancelando(plantao);
    cancelarDisclosure.onOpen();
  };
  const atualizarStatus = (plantao, acao, titulo) => {
    statusMutation.mutate(
      { id: plantao.id, acao },
      {
        onSuccess: () => toast({ title: titulo, status: "success", duration: 4000 }),
        onError: (error) =>
          toast({
            title: "Não foi possível atualizar o plantão",
            description: error.response?.data?.mensagem,
            status: "error",
            duration: 5000,
            isClosable: true,
          }),
      }
    );
  };

  const shiftActions = {
    onEdit: abrirEditar,
    onReschedule: abrirReagendar,
    onCancel: abrirCancelar,
    onInvoiceIssued: (plantao) => atualizarStatus(plantao, "marcar_nf_emitida", "NF marcada como emitida"),
    onReceived: (plantao) => atualizarStatus(plantao, "marcar_recebido", "Plantão marcado como recebido"),
    onDuplicate: abrirDuplicar,
  };
  // Alinha o dia 1 ao dia da semana correto na grade de 7 colunas.
  const offsetPrimeiroDia = new Date(ano, mes - 1, 1).getDay();
  const [previousLabel, nextLabel] = NAV_LABELS[view];
  const periodLabel = view === "semana" ? `${shortDate(week[0])} – ${shortDate(week[6])}/${week[6].slice(0, 4)}` : formatarMesAno(ano, mes);
  const isLoading = view === "semana" ? weekQuery.isLoading : workedQuery.isLoading || (view === "mes" && paymentQuery.isLoading);
  // Sem isto, uma falha em /plantoes mostrava o mês vazio e o resumo zerado, que se leem
  // como "não houve plantão". Cada visão depende só das consultas que ela usa.
  const viewQueries = view === "semana" ? [weekQuery] : view === "mes" ? [workedQuery, paymentQuery] : [workedQuery];
  const failedQueries = viewQueries.filter((query) => query.isError);
  const retryFailed = () => failedQueries.forEach((query) => query.refetch());
  const summaryFailed = workedQuery.isError || paymentQuery.isError;

  return (
    <>
      <PageHeader
        title="Calendário de recebimentos"
        description="Plantões realizados e pagamentos previstos, dia a dia. Clique em um dia para registrar um plantão."
        actions={
          <>
            <ImportFromGoogleButton year={ano} month={mes} />
            <Button variant="outline" leftIcon={<Repeat size={16} />} onClick={gerarEscalaDisclosure.onOpen}>
              Gerar escala do mês
            </Button>
            <Button variant="outline" leftIcon={<CalendarPlus size={16} />} onClick={() => abrirNovoCompromisso(null)}>
              Novo compromisso
            </Button>
            <Button leftIcon={<Plus size={16} />} onClick={() => abrirRegistrar(null)}>
              Registrar plantão
            </Button>
          </>
        }
      />

      {/* Na Semana as 7 colunas precisam da largura toda: legenda e resumo descem. */}
      <Grid templateColumns={{ base: "1fr", lg: view === "semana" ? "1fr" : "3fr 1fr" }} gap={5}>
        <GridItem minW={0}>
          <SectionCard p={{ base: 3, md: 5 }}>
            <HStack justify="space-between" mb={4} spacing={2} flexWrap="wrap" rowGap={2}>
              <CalendarViewSwitcher view={view} onChange={setView} />
              <HStack spacing={1}>
                <IconButton aria-label={previousLabel} icon={<ChevronLeft size={18} />} variant="ghost" onClick={previous} />
                <Heading size="sm" minW={{ md: "170px" }} textAlign="center">
                  {periodLabel}
                </Heading>
                <IconButton aria-label={nextLabel} icon={<ChevronRight size={18} />} variant="ghost" onClick={next} />
              </HStack>
              <Button size="sm" variant="ghost" onClick={goToday}>
                Hoje
              </Button>
            </HStack>

            <Wrap spacing={2} mb={4}>
              <WrapItem>
                <VinculoFilterMenu vinculos={vinculos ?? []} selectedIds={vinculoIds} onChange={setVinculoIds} />
              </WrapItem>
              <WrapItem>
                <Button size="sm" variant={statusFiltro === "" ? "solid" : "outline"} onClick={() => setStatusFiltro("")}>
                  Todos
                </Button>
              </WrapItem>
              {STATUS_ORDEM.map((status) => (
                <WrapItem key={status}>
                  <Button
                    size="sm"
                    variant={statusFiltro === status ? "solid" : "outline"}
                    onClick={() => setStatusFiltro(statusFiltro === status ? "" : status)}
                  >
                    {STATUS_META[status].label}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>

            {failedQueries.length > 0 ? (
              <EmptyState
                icon={AlertTriangle}
                title="Não foi possível carregar os plantões."
                description="Verifique a conexão e tente de novo."
                action={
                  <Button size="sm" variant="outline" onClick={retryFailed}>
                    Tentar de novo
                  </Button>
                }
              />
            ) : isLoading ? (
              <Skeleton height="400px" borderRadius="md" />
            ) : view === "semana" ? (
              <WeekView
                dates={week}
                today={today}
                shiftsByDate={weekShiftsByDate}
                events={compromissos ?? []}
                vinculosById={vinculosById}
                shiftActions={shiftActions}
                onEditEvent={abrirEditarCompromisso}
                onDeleteEvent={abrirExcluirCompromisso}
                onRegister={abrirRegistrar}
                onAddEvent={abrirNovoCompromisso}
              />
            ) : view === "lista" ? (
              <DayListView
                list={dayList}
                today={today}
                vinculosById={vinculosById}
                shiftActions={shiftActions}
                onEditEvent={abrirEditarCompromisso}
                onDeleteEvent={abrirExcluirCompromisso}
              />
            ) : (
              <>
                <SimpleGrid columns={7} spacing={1} mb={1}>
                  {DIAS_SEMANA_ABREV.map((d) => (
                    <Text key={d} fontSize="xs" fontWeight="semibold" color="app-muted-fg" textAlign="center">
                      {d}
                    </Text>
                  ))}
                </SimpleGrid>
                <SimpleGrid columns={7} spacing={1}>
                  {Array.from({ length: offsetPrimeiroDia }).map((_, i) => (
                    <Box key={`vazio-${i}`} />
                  ))}
                  {calendar.days.map((dia) => (
                    <CalendarDayCell
                      key={dia.dia}
                      day={dia}
                      shifts={plantoesPorDia[dia.dia] ?? []}
                      {...shiftActions}
                      onRegister={() => abrirRegistrar(isoDate(ano, mes, dia.dia))}
                      vinculosById={vinculosById}
                      events={compromissosPorDia[dia.dia] ?? []}
                      onEditEvent={abrirEditarCompromisso}
                      onDeleteEvent={abrirExcluirCompromisso}
                      onAddEvent={() => abrirNovoCompromisso(isoDate(ano, mes, dia.dia))}
                    />
                  ))}
                </SimpleGrid>
              </>
            )}
          </SectionCard>
        </GridItem>

        <GridItem>
          <VStack align="stretch" spacing={5}>
            <SectionCard title="Legenda">
              <VStack align="stretch" spacing={2}>
                <HStack spacing={2}>
                  <Box boxSize="10px" borderRadius="full" bg="primary" />
                  <Text fontSize="sm" color="app-muted-fg">
                    Plantão realizado
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Box boxSize="10px" borderRadius="full" bg="teal.400" />
                  <Text fontSize="sm" color="app-muted-fg">
                    Pagamento previsto
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Box boxSize="10px" borderRadius="sm" bg="app-muted" />
                  <Text fontSize="sm" color="app-muted-fg">
                    Compromisso (não entra no faturamento)
                  </Text>
                </HStack>
              </VStack>
            </SectionCard>

            <SectionCard
              title={`Resumo de ${formatarMesAno(ano, mes)}`}
              action={
                isFiltered ? (
                  <Badge variant="subtle" colorScheme="brand" title="O resumo considera só os filtros escolhidos">
                    Filtrado
                  </Badge>
                ) : null
              }
            >
              {summaryFailed ? (
                <Text fontSize="sm" color="app-muted-fg" data-testid="month-summary">
                  Não foi possível carregar o resumo do mês.
                </Text>
              ) : (
                <VStack align="stretch" spacing={3} data-testid="month-summary">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="app-muted-fg">
                      Plantões
                    </Text>
                    <Text fontWeight="semibold">{calendar.summary.plantoes}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="app-muted-fg">
                      Faturamento
                    </Text>
                    <Text fontWeight="semibold">
                      <CurrencyText value={calendar.summary.faturamento} />
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="app-muted-fg">
                      Dias de recebimento
                    </Text>
                    <Text fontWeight="semibold">{calendar.summary.dias_de_recebimento}</Text>
                  </HStack>
                </VStack>
              )}
            </SectionCard>
          </VStack>
        </GridItem>
      </Grid>

      <EditShiftModal isOpen={editarDisclosure.isOpen} onClose={editarDisclosure.onClose} shift={plantaoEditando} />
      <EditShiftModal
        isOpen={reagendarDisclosure.isOpen}
        onClose={reagendarDisclosure.onClose}
        shift={plantaoReagendando}
        rescheduleOnly
      />
      <CancelShiftDialog isOpen={cancelarDisclosure.isOpen} onClose={cancelarDisclosure.onClose} shift={plantaoCancelando} />
      <RegistrarPlantaoModal
        isOpen={registrarDisclosure.isOpen}
        onClose={registrarDisclosure.onClose}
        dataInicial={dataRegistro}
        plantaoBase={plantaoDuplicando}
      />
      <CalendarEventModal
        isOpen={compromissoDisclosure.isOpen}
        onClose={compromissoDisclosure.onClose}
        event={compromissoEditando}
        dataInicial={dataCompromisso}
      />
      <DeleteCalendarEventDialog
        isOpen={excluirCompromissoDisclosure.isOpen}
        onClose={excluirCompromissoDisclosure.onClose}
        event={compromissoExcluindo}
      />
      <GenerateScheduleModal isOpen={gerarEscalaDisclosure.isOpen} onClose={gerarEscalaDisclosure.onClose} initialYear={ano} initialMonth={mes} />
    </>
  );
}
